import {
  getBasePath,
} from 'web-utils';
import {
  UsersPage,
  VideosPage,
  MessagesPage,
  PaymentsPage,
  ProductsPage,
  DashboardPage,
  ExercisesPage,
  MarketplacePage,
  EventResultsPage,
  PersonalProgramsPage,
  PersonalWorkoutsPage,

  // PRO
  TagPage,
  BillingPage,
  ClientsPage,
  CalendarPage,
  DocumentPage,
  SearchResults,
  EntityFolderPage,
  ClientGroupsPage,
  DocumentsListPage,
  NewAnnouncementPage,
  WorkoutTemplatesPage,
  ProgramTemplatesPage,
  PersonalPropertiesPage,

  // CLIENT
  AccountPage,
  PersonalEntityPage,
} from 'web-containers';

const entityFolders = [
  {
    type: 'exercises',
    path: 'exercises',
  },
  {
    type: 'workoutTemplates',
    path: 'workout_templates',
  },
  {
    type: 'warmups',
    path: 'warmups',
  },
  {
    type: 'cooldowns',
    path: 'cooldowns',
  },
  {
    type: 'programTemplates',
    path: 'program_templates',
  },
];

const clientResourceTypes = [
  'personalExercises',
  'personalWarmups',
  'personalWorkouts',
  'personalCooldowns',
  'personalPrograms',
];

export interface Route {
  path: string;
  exact?: boolean;
  onlyPro?: boolean;
  onlyClient?: boolean;
  preload?: boolean;
  options?: any;
  redirect?: string;
  component?: any;
  statusLocations?: string[];
}

const privateRoutes: Array<Route> = [
  // COMMON ROUTES
  {
    path: '/welcome',
    preload: true,
    component: DashboardPage,
  },
  {
    path: '/personal_workouts/:id',
    preload: true,
    component: PersonalWorkoutsPage,
  },
  {
    path: '/workout_exercises/:id',
    options: {
      resourceType: 'workoutExercises',
    },
    preload: true,
    component: ExercisesPage,
  },
  {
    path: '/personal_programs/:id',
    component: PersonalProgramsPage,
  },
  {
    path: '/events/:id',
    component: EventResultsPage,
  },
  {
    path: '/workout_events/:id',
    component: EventResultsPage,
  },
  {
    path: '/users/:id',
    preload: true,
    component: UsersPage,
  },
  {
    exact: true,
    path: '/payments',
    redirect: '/payments/transactions',
  },
  {
    path: '/payments/:tab',
    preload: true,
    component: PaymentsPage,
  },
  {
    path: '/products',
    component: ProductsPage,
  },
  {
    path: '/marketplace',
    preload: true,
    component: MarketplacePage,
  },
  {
    exact: true,
    path: '/videos',
    redirect: '/videos/recent',
  },
  {
    path: '/videos/:order/:page?',
    preload: true,
    component: VideosPage,
  },

  // PRO ROUTES
  {
    path: '/billing',
    exact: true,
    redirect: '/billing/invoices',
  },
  {
    path: '/billing/:tab?',
    onlyPro: true,
    preload: true,
    component: BillingPage,
  },
  ...entityFolders.map(
    folder => ({
      path: `/${folder.path}_folder/:id`,
      options: {
        resourceType: folder.type,
      },
      onlyPro: true,
      preload: true,
      component: EntityFolderPage,
      statusLocations: [
        'folders.requests.fetchLibrary.status',
      ],
    }),
  ),
  {
    path: '/clients',
    exact: true,
    redirect: '/clients/individuals',
  },
  {
    path: '/clients/:tab?',
    onlyPro: true,
    preload: true,
    component: ClientsPage,
    statusLocations: [
      'clients.requests.fetchClients.status',
    ],
  },
  {
    path: '/client_groups/:id',
    onlyPro: true,
    preload: true,
    component: ClientGroupsPage,
    statusLocations: [
      'clientGroups.requests.fetchClientGroups.status',
    ],
  },
  {
    path: '/messages',
    // onlyPro: true,
    preload: true,
    component: MessagesPage,
  },
  {
    path: '/calendar',
    onlyPro: true,
    preload: true,
    component: CalendarPage,
  },
  {
    path: '/exercises/:id',
    options: {
      resourceType: 'exercises',
    },
    onlyPro: true,
    component: ExercisesPage,
  },
  {
    path: '/workout_templates/:id',
    options: {
      resourceType: 'workoutTemplates',
    },
    onlyPro: true,
    preload: true,
    component: WorkoutTemplatesPage,
  },
  {
    path: '/warmups/:id',
    options: {
      resourceType: 'warmups',
    },
    onlyPro: true,
    component: WorkoutTemplatesPage,
  },
  {
    path: '/cooldowns/:id',
    onlyPro: true,
    options: {
      resourceType: 'cooldowns',
    },
    component: WorkoutTemplatesPage,
  },
  {
    path: '/program_templates/:id',
    onlyPro: true,
    preload: true,
    component: ProgramTemplatesPage,
  },
  {
    path: '/personal_properties',
    onlyPro: true,
    component: PersonalPropertiesPage,
    preload: true,
    statusLocations: [
      'personalProperties.requests.fetchPersonalProperties.status',
    ],
  },
  {
    path: '/search/:searchQuery',
    onlyPro: true,
    preload: true,
    component: SearchResults,
  },
  {
    path: '/tags/:tag',
    onlyPro: true,
    preload: true,
    component: TagPage,
  },
  {
    path: '/documents/:id',
    onlyPro: true,
    preload: true,
    component: DocumentPage,
  },
  {
    path: '/documents_folder/:id',
    onlyPro: true,
    preload: true,
    component: DocumentsListPage,
    statusLocations: [
      'folders.requests.fetchLibrary.status',
    ],
  },
  {
    path: '/new_announcement',
    onlyPro: true,
    component: NewAnnouncementPage,
  },

  // CLIENT ROUTES
  ...clientResourceTypes.map(
    resourceType => ({
      path: getBasePath(resourceType),
      exact: true,
      options: {
        resourceType,
      },
      component: PersonalEntityPage,
      onlyClient: true,
      preload: true,
      statusLocations: [
        `${resourceType}.requests.fetchPersonalEntity.status`,
        'pros.requests.fetchPros.status',
      ],
    }),
  ),
  {
    path: '/personal_exercises/:id',
    options: {
      requestType: 'exercises',
      resourceType: 'personalExercises',
    },
    component: ExercisesPage,
    onlyClient: true,
  },
  {
    path: '/personal_warmups/:id',
    options: {
      resourceType: 'personalWorkouts',
      addtionalType: 'personalWarmups',
    },
    component: PersonalWorkoutsPage,
    onlyClient: true,
  },
  {
    path: '/personal_cooldowns/:id',
    options: {
      resourceType: 'personalWorkouts',
      addtionalType: 'personalCooldowns',
    },
    component: PersonalWorkoutsPage,
    onlyClient: true,
  },
  {
    path: '/account',
    preload: true,
    component: AccountPage,
    onlyClient: true,
  },
  {
    path: '/documents',
    component: DocumentsListPage,
    onlyClient: true,
  },
];

export default privateRoutes;
